import { aggregateRowsByGrouping } from './aggregation';
import { createSubtotalRow } from './subtotal';
import { doClientSidePivot } from 'common/visualizations/helpers/PivotHelpers';
import { groupRowsByKey } from './groupKey';
import { type ColumnVO, type IServerSideGetRowsRequest } from 'ag-grid-community';
import { type HeaderRow, type NormalRow, type Row } from '../type';
import { type SubtotalsData } from 'common/visualizations/views/agGridReact/types';
import { type TableColumn } from '../../types';
import { discoverPivotFieldsFromSubtotals, populateLeafPivotValues } from './subtotalUtils';
import { PIVOT_PAGE_SIZE_IN_AX } from 'common/visualizations/views/agGridReact/Constants';

/**
 * Checks if processing should be limited based on current state and grouping configuration.
 */
const shouldLimitGroupProcessing = (
  level: number,
  context: GroupProcessorContext,
  state: GroupProcessorState,
): boolean => {
  if (level !== 0) {
    return false;
  }

  if (context.rowGroupCols.length === 1) {
    // Single group level: limit total rows to 1000
    return state.allPivotedRows.length >= PIVOT_PAGE_SIZE_IN_AX;
  }

  // Multiple group levels: limit level 0 groups to 1000
  return state.level0HeaderCount >= PIVOT_PAGE_SIZE_IN_AX;
};

/**
 * Context passed through group processing operations containing configuration and request data.
 */
export interface GroupProcessorContext {
  fakeAgRequest: IServerSideGetRowsRequest;
  rowGroupCols: ColumnVO[];
  groupColumnIds: string[];
  showSubTotal: boolean;
  pivotTableColumns: TableColumn[];
  subtotalLookup?: SubtotalsData;
  isTablePrintingImprovementGroupingLayoutEnabled?: boolean;
}

/**
 * Mutable state accumulated during recursive group processing.
 */
interface GroupProcessorState {
  allPivotedRows: Row[];
  allPivotFields: string[];
  level0HeaderCount: number;
}

/**
 * Creates a normalized group value (handles null conversion).
 */
const normalizeGroupValue = (groupKey: string): string | null => {
  return groupKey === 'null' ? null : groupKey;
};

/**
 * Determines if pivot fields should be updated.
 */
const shouldUpdatePivotFields = (state: GroupProcessorState): boolean => {
  return !state.allPivotFields.length;
};

/**
 * Creates a leaf row with group values and pivot data.
 */
const createLeafRowWithPivotData = (
  groupKeys: string[],
  leafLevel: number,
  context: GroupProcessorContext,
): NormalRow | HeaderRow => {
  const { groupColumnIds, subtotalLookup, isTablePrintingImprovementGroupingLayoutEnabled } = context;
  const leafRow: NormalRow | HeaderRow = {
    _groupLevel: leafLevel,
    _groupKeys: groupKeys,
    ...(isTablePrintingImprovementGroupingLayoutEnabled && {
      _rowType: 'header' as const,
    }),
  };

  setGroupColumnValue(leafRow, groupKeys[leafLevel], groupColumnIds[leafLevel]);
  populateLeafPivotValues(leafRow, groupKeys, leafLevel, subtotalLookup!);

  return leafRow;
};

/**
 * Processes the leaf level of a group hierarchy by using pre-computed subtotal values.
 */
const processLeafLevel = (
  groupedRows: NormalRow[],
  groupKeys: string[],
  context: GroupProcessorContext,
  state: GroupProcessorState,
): void => {
  if (!context.subtotalLookup) {
    processLeafLevelFallback(groupedRows, groupKeys, context, state);
    return;
  }

  const leafLevel = context.rowGroupCols.length - 1;
  const leafRow = createLeafRowWithPivotData(groupKeys, leafLevel, context);

  updatePivotFieldsFromSubtotals(state, groupKeys, context.subtotalLookup);
  state.allPivotedRows.push(leafRow);
};

/**
 * Performs client-side pivot processing when subtotal lookup is unavailable.
 */
const processLeafLevelFallback = (
  groupedRows: NormalRow[],
  groupKeys: string[],
  context: GroupProcessorContext,
  state: GroupProcessorState,
): void => {
  const pivotResult = performClientSidePivot(groupedRows, groupKeys, context);

  state.allPivotedRows.push(...pivotResult.pivotedRows);

  if (shouldUpdatePivotFields(state)) {
    state.allPivotFields = pivotResult.pivotFields;
  }
};

/**
 * Performs the actual client-side pivot operation.
 */
const performClientSidePivot = (
  groupedRows: NormalRow[],
  groupKeys: string[],
  context: GroupProcessorContext,
) => {
  const requestWithGroupKeys = { ...context.fakeAgRequest, groupKeys };
  const aggregatedRows = aggregateRowsByGrouping(
    groupedRows,
    context.fakeAgRequest,
    context.pivotTableColumns,
  );
  return doClientSidePivot(requestWithGroupKeys, aggregatedRows);
};

/**
 * Sets a group column value in the row, handling null conversion.
 */
const setGroupColumnValue = (row: Row, groupKey: string, columnId: string): void => {
  row[columnId] = normalizeGroupValue(groupKey);
};

/**
 * Updates pivot fields from subtotal data for consistent field tracking.
 */
const updatePivotFieldsFromSubtotals = (
  state: GroupProcessorState,
  groupKeys: string[],
  subtotalLookup: SubtotalsData,
): void => {
  if (shouldUpdatePivotFields(state)) {
    const parentGroupKeys = groupKeys.slice(0, 1);
    state.allPivotFields = discoverPivotFieldsFromSubtotals(subtotalLookup, parentGroupKeys, 0);
  }
};

/**
 * Processes a non-leaf level of the group hierarchy by creating a header row,
 * recursing into child groups, and optionally adding a subtotal row.
 *
 * @param groupedRows - Rows belonging to this group
 * @param groupKey - Current group's key value
 * @param groupKeys - Accumulated keys including this group
 * @param level - Current depth in the group hierarchy
 * @param currentGroupColumn - Column ID being grouped at this level
 * @param context - Configuration and request context
 * @param state - Mutable state to accumulate pivoted rows and fields
 *
 * @example
 * // Process non-leaf level for region 'West'
 * processNonLeafLevel(
 *   [{ region: 'West', category: 'A', sales: 100 }],
 *   'West',
 *   ['West'],
 *   0,
 *   'region',
 *   context,
 *   state
 * );
 * // state.allPivotedRows now contains: [header row for 'West', nested data rows, subtotal row]
 */
const processNonLeafLevel = (
  groupedRows: NormalRow[],
  groupKey: string,
  groupKeys: string[],
  level: number,
  currentGroupColumn: string,
  context: GroupProcessorContext,
  state: GroupProcessorState,
): void => {
  const groupValue = groupKey === 'null' ? null : groupKey;
  const headerRow: HeaderRow = {
    [currentGroupColumn]: groupValue,
    _rowType: 'header',
    _groupLevel: level,
    _groupKeys: groupKeys,
  };

  state.allPivotedRows.push(headerRow);

  if (level === 0) {
    state.level0HeaderCount++;
  }

  const nestedState: GroupProcessorState = {
    allPivotedRows: state.allPivotedRows,
    allPivotFields: [],
    level0HeaderCount: state.level0HeaderCount,
  };

  processGroupLevelRecursive(groupedRows, groupKeys, level + 1, context, nestedState);

  const newFields = nestedState.allPivotFields.filter((field) => !state.allPivotFields.includes(field));
  state.allPivotFields.push(...newFields);

  if (context.showSubTotal && context.subtotalLookup) {
    // Use server-side subtotal data as the source for pivot fields
    const serverPivotFields = discoverPivotFieldsFromSubtotals(context.subtotalLookup, groupKeys, level);

    if (serverPivotFields.length > 0) {
      const subtotalRow = createSubtotalRow(
        groupKeys,
        context.groupColumnIds,
        serverPivotFields,
        level,
        context.subtotalLookup,
      );

      state.allPivotedRows.push(subtotalRow);
    }
  }
};

/**
 * Recursively processes a single level of the group hierarchy, splitting rows by the current group column.
 *
 * @param remainingRows - Rows to group at this level
 * @param groupKeys - Accumulated keys from parent groups
 * @param level - Current depth in the group hierarchy
 * @param context - Configuration and request context
 * @param state - Mutable state to accumulate pivoted rows and fields
 *
 * @example
 * // Process first level of grouping by 'region'
 * processGroupLevelRecursive(
 *   [{ region: 'West', sales: 100 }, { region: 'East', sales: 200 }],
 *   [],
 *   0,
 *   context,
 *   state
 * );
 * // Iterates over each unique region and processes its rows
 */
const processGroupLevelRecursive = (
  remainingRows: NormalRow[],
  groupKeys: string[],
  level: number,
  context: GroupProcessorContext,
  state: GroupProcessorState,
): void => {
  const isLeafLevel = level === context.rowGroupCols.length - 1;
  const currentGroupColumn = context.rowGroupCols[level].id;
  const groupedRowsMap = groupRowsByKey(remainingRows, currentGroupColumn);
  for (const [groupKey, groupedRows] of groupedRowsMap) {
    if (shouldLimitGroupProcessing(level, context, state)) {
      return;
    }

    const groupKeyForLookup = groupKey === 'null' ? 'null' : groupKey;
    const newGroupKeys = [...groupKeys, groupKeyForLookup];

    if (isLeafLevel) {
      processLeafLevel(groupedRows, newGroupKeys, context, state);
    } else {
      processNonLeafLevel(groupedRows, groupKey, newGroupKeys, level, currentGroupColumn, context, state);
    }
  }
};

/**
 * Entry point for processing grouped rows into a hierarchical pivot structure with headers and subtotals.
 *
 * @param remainingRows - Rows to process
 * @param groupKeys - Initial group keys (typically empty array at top level)
 * @param level - Starting level in the group hierarchy
 * @param context - Configuration including row group columns, pivot columns, and subtotal settings
 * @returns Processed pivot fields and flattened rows including headers, data, and subtotals
 *
 * @example
 * const result = processGroupLevel(
 *   [{ region: 'West', category: 'A', sales: 100 }],
 *   [],
 *   0,
 *   { rowGroupCols: [{ id: 'region' }], showSubTotal: true, ... }
 * );
 * // result.processedRows: [header for 'West', data rows, subtotal, ...]
 * // result.processedFields: ['sales@@2024', 'sales@@2025', ...]
 */
export const processGroupLevel = (
  remainingRows: NormalRow[],
  groupKeys: string[],
  level: number,
  context: GroupProcessorContext,
): { processedFields: string[]; processedRows: Row[] } => {
  const state: GroupProcessorState = {
    allPivotedRows: [],
    allPivotFields: [],
    level0HeaderCount: 0,
  };

  processGroupLevelRecursive(remainingRows, groupKeys, level, context, state);

  // Trim to 1000 pivot rows (first level)
  const level0HeaderIndices: number[] = [];
  const level0Headers = state.allPivotedRows.filter((row, index) => {
    const isLevel0Header = row._groupLevel === 0 && row._rowType === 'header';
    if (isLevel0Header) {
      level0HeaderIndices.push(index);
    }
    return isLevel0Header;
  });

  if (level0Headers.length > PIVOT_PAGE_SIZE_IN_AX) {
    // Cut at the (PIVOT_PAGE_SIZE_IN_AX + 1)th header to keep only first PIVOT_PAGE_SIZE_IN_AX complete groups
    if (level0HeaderIndices.length > PIVOT_PAGE_SIZE_IN_AX) {
      const cutoffIndex = level0HeaderIndices[PIVOT_PAGE_SIZE_IN_AX]; // Index of 1001st header
      state.allPivotedRows.splice(cutoffIndex); // Remove everything from 1001st header onward including leaf rows and subtotals
    }
  }

  return {
    processedFields: state.allPivotFields,
    processedRows: state.allPivotedRows,
  };
};
